import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import { formatCurrencyInput, hasBreakdownData } from "@rebuild/shared";
import { EditableText } from "../../EditableText";
import type { PDFPreviewFormData } from "../pdfPreview.types";

interface PDFTotalCostEditorProps {
  control: Control<PDFPreviewFormData>;
  // From previewData (strings from API)
  previewDataOverhead?: string;
  previewDataProfit?: string;
  // Calculated values for display
  overhead: string;
  profit: string;
  taxAmount: string;
  total: string;
  // Flags
  isSubtotalEditable: boolean;
  isTotalEditable: boolean;
  includeTax: boolean;
  includeOverheadAndProfit: boolean;
  overheadPercent: number;
  profitPercent: number;
}

export function PDFTotalCostEditor({
  control,
  previewDataOverhead,
  previewDataProfit,
  overhead,
  profit,
  taxAmount,
  total,
  isSubtotalEditable,
  isTotalEditable,
  includeTax,
  includeOverheadAndProfit,
  overheadPercent,
  profitPercent,
}: PDFTotalCostEditorProps) {
  const showBreakdown =
    previewDataOverhead !== undefined &&
    previewDataProfit !== undefined &&
    hasBreakdownData({ overhead: previewDataOverhead, profit: previewDataProfit });
  const taxRateEditor = includeTax ? (
    <Controller
      name="taxRate"
      control={control}
      render={({ field: { onChange, value } }) => (
        <div className="flex items-center text-base">
          <span>Sales Tax (</span>
          <EditableText value={value} onChange={onChange} inputType="percent" editClassName="mx-1 w-16" suffix="%" />
          <span>): ${formatCurrencyInput(taxAmount || "0.00")}</span>
        </div>
      )}
    />
  ) : null;

  if (showBreakdown) {
    return (
      <div className="mt-8 pt-4">
        <div className="space-y-1">
          {/* Subtotal - editable if not room-grouped */}
          <Controller
            name="subtotal"
            control={control}
            render={({ field: { onChange, value } }) =>
              isSubtotalEditable ? (
                <div className="flex items-center text-base">
                  <span className="mr-2">Subtotal:</span>
                  <EditableText value={value} onChange={onChange} inputType="currency" prefix="$" editClassName="w-32" />
                </div>
              ) : (
                <p className="text-base">Subtotal: ${formatCurrencyInput(value || "0.00")}</p>
              )
            }
          />
          {includeOverheadAndProfit && (
            <>
              <p className="text-base">
                Overhead ({overheadPercent}%): ${formatCurrencyInput(overhead)}
              </p>
              <p className="text-base">
                Profit ({profitPercent}%): ${formatCurrencyInput(profit)}
              </p>
            </>
          )}
          {taxRateEditor}
          <div className="group relative inline-block pt-2 text-base font-bold" title="Only edit individual line item prices">
            Total Cost Estimate: ${formatCurrencyInput(total)}
            <div className="absolute bottom-full left-0 z-10 mb-2 hidden rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white group-hover:block">
              Only edit individual line item prices
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standard display (non-Reconstruction or legacy)
  return (
    <div className="mt-8 pt-4">
      <Controller
        name="subtotal"
        control={control}
        render={({ field: { onChange, value } }) => (
          <div className="space-y-1">
            {taxRateEditor}
            <div className="text-base font-bold">
              {isTotalEditable ? (
                <div className="flex items-center">
                  <span className="mr-2">Total Cost Estimate:</span>
                  <EditableText value={value} onChange={onChange} inputType="currency" prefix="$" editClassName="w-32" />
                </div>
              ) : (
                <div className="group relative inline-block" title="Only edit individual line item prices">
                  Total Cost Estimate: ${formatCurrencyInput(total)}
                  <div className="absolute bottom-full left-0 z-10 mb-2 hidden rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white group-hover:block">
                    Only edit individual line item prices
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      />
    </div>
  );
}
